import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import {
  graphDataInterface,
  GraphState,
  NewFloorPlanItem,
  PollingProgress,
} from "./graph.model";
import { fetchGraphData } from "../../api/api.graph";

// Initial state
const initialState: GraphState = {
  floorPlans: [],
  selectedFloorPlan: [],
  lastFetch: "",
  loading: false,
  isError: false,
  error: null,
  index: 0,
  dimensioned: false,
  fromDocuments: false,
  fromNewInputEditor: false,
  documentId: -1,
  graphData: {
    corridor: false,
    dimensioned: false,
    dimensionedCirculation: false,
    minDimEnabled: false,
    removeAddCirculation: false,
    publicEnabled: false,
    normalizeConst: false,
    limit: 0,
    corridorThickness: 0,
    starting_from: 0,
    count: 0,
    rectangular: false,
    nodes: [],
    edges: [],
  },
  pollingProgress: null,
  roomNameMapping: {},
};

// Define a thunk for the API request

// Slice
export const graphSlice = createSlice({
  name: "graph",
  initialState,
  reducers: {
    setDimensioned: (state, action: PayloadAction<boolean>) => {
      state.dimensioned = action.payload;
    },
    setLastFetch: (state, action: PayloadAction<string>) => {
      state.lastFetch = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.isError = true;
      state.error = action.payload;
    },
    setIndex: (state, action: PayloadAction<number>) => {
      console.log(action.payload);
      state.index = action.payload;
    },
    setFloorPlan: (state, action: PayloadAction<NewFloorPlanItem[][]>) => {
      state.floorPlans = action.payload;
    },
    setSelectedFloorPlan: (
      state,
      action: PayloadAction<NewFloorPlanItem[]>,
    ) => {
      state.selectedFloorPlan = action.payload;
    },
    deleteSelectedFloorPlan: (state) => {
      state.selectedFloorPlan = [];
    },
    setFromDocuments: (state, action: PayloadAction<boolean>) => {
      state.fromDocuments = action.payload;
    },
    setFromNewInputEditor: (state, action: PayloadAction<boolean>) => {
      state.fromNewInputEditor = action.payload;
    },
    setDocumentID: (state, action: PayloadAction<number>) => {
      state.documentId = action.payload;
    },
    setGraphData: (state, action: PayloadAction<graphDataInterface>) => {
      state.graphData = action.payload;
    },
    setPollingProgress: (state, action: PayloadAction<PollingProgress | null>) => {
      state.pollingProgress = action.payload;
    },
    setRoomNameMapping: (state, action: PayloadAction<Record<number, string>>) => {
    state.roomNameMapping = action.payload;
},
    updateSelectedFloorPlan: (state, action: PayloadAction<Partial<NewFloorPlanItem>[]>) => {
      state.selectedFloorPlan = state.selectedFloorPlan.map((item, index) => ({
        ...item,
        ...(action.payload[index] || {})
      }));
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGraphData.pending, (state) => {
        state.loading = true;
        state.pollingProgress = null;
      })
      .addCase(fetchGraphData.fulfilled, (state, action) => {
        state.floorPlans = action.payload;
        state.isError = false;
        state.loading = false;
        state.pollingProgress = null;
        state.lastFetch = new Date().toISOString();
      })
      .addCase(fetchGraphData.rejected, (state, action) => {
        state.loading = false;
        state.isError = true;
        state.pollingProgress = null;
        state.error = (action.payload as string) || action.error.message || "Failed to fetch graph data";
      });
  },
});

// Export actions
export const {
  setLastFetch,
  setLoading,
  setError,
  setIndex,
  setDimensioned,
  setFloorPlan,
  setSelectedFloorPlan,
  deleteSelectedFloorPlan,
  setGraphData,
  setPollingProgress,
  setRoomNameMapping,
  setFromDocuments,
  setFromNewInputEditor,
  setDocumentID,
  updateSelectedFloorPlan,
  reset,
} = graphSlice.actions;

// Export reducer
export default graphSlice.reducer;
